import { SyncOutlined } from "@mui/icons-material";
import { Button, Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useAllowedSectors, useDebounce } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { IAgreement, SinisterAgreementService } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { SinisterSector, getSectorLabel } from "../../creditRecovery/constants/sectorOptions";
import { resolveDatesFromData } from "../../sinister/helpers/resolveDatesFromData";
import { ToolbarSinisterGeneric } from "../../sinister/components/ToolbarSinisterGeneric/ToolbarSinisterGeneric";
import { fieldsConfig } from "./fieldsConfig";
import { ModalSyncAgreementBillets } from "./Modals/ModalSyncAgreementBillets";
import { TableAgreements } from "./TableAgreements";

export const AgreementsListPresentation = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();

	const pagina = Number(searchParams.get("pagina") || "1");
	const limit = Number(searchParams.get("limit") || "10");
	const statusCsv = searchParams.get("status") || "Todos";
	const paymentMethod = searchParams.get("paymentMethod") || "Todos";
	const filterField = (searchParams.get("filterField") || "documento") as "documento" | "nome";
	const textSearch = searchParams.get("textSearch") || "";

	const data = searchParams.get("data") || "ano";
	const isPeriodo = data === "periodo";
	const resolvedDates = isPeriodo
		? { dataIni: searchParams.get("dataIni") || "", dataFim: searchParams.get("dataFim") || "" }
		: resolveDatesFromData(data);

	const filtersFromSearchParams = {
		status: statusCsv.split(","),
		paymentMethod,
		filterField,
		textSearch,
		data,
		dataIni: resolvedDates.dataIni,
		dataFim: resolvedDates.dataFim,
	};

	const [rows, setRows] = useState<IAgreement[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [shouldFetch, setShouldFetch] = useState(true);
	const [selectedSector, setSelectedSector] = useState<SinisterSector>("cobranca");
	const [openSyncBilletsModal, setOpenSyncBilletsModal] = useState(false);
	const { isSuperAdmin } = useProfile();
	const hasViewPermission = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.AgreementsOptions, PermissionActionsEnum.View);
	const canSyncBillets = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.AgreementsOptions, PermissionActionsEnum.SyncAgreementBillets);

	// Abas visíveis conforme as permissões por setor do perfil — mesmo critério da listagem de Cobrança/Jurídico.
	const allowedSectors = useAllowedSectors(PermissionRolesEnum.AgreementsOptions);
	const activeSector = allowedSectors.includes(selectedSector) ? selectedSector : allowedSectors[0];

	// As permissões chegam depois do primeiro render: se a aba inicial não for permitida, cai na primeira liberada.
	useEffect(() => {
		if (allowedSectors.includes(selectedSector)) return;

		setSelectedSector(allowedSectors[0]);
		setShouldFetch(true);
	}, [allowedSectors, selectedSector]);

	useEffect(() => {
		if (!shouldFetch) return;

		setIsLoading(true);
		debounce(() => {
			SinisterAgreementService.getAll(
				pagina,
				limit,
				statusCsv,
				paymentMethod,
				filterField,
				textSearch,
				resolvedDates.dataIni,
				resolvedDates.dataFim,
				selectedSector
			).then((result) => {
				if (result instanceof Error) {
					alert(result.message);
				} else {
					setTotalCount(result.totalCount);
					setRows(result.data);
				}
				setIsLoading(false);
				setShouldFetch(false);
			});
		});
	}, [shouldFetch]);

	const handleClearFilters = () => {
		setSearchParams({}, { replace: true });
		setShouldFetch(true);
	};

	const handleSearch = (filters: Record<string, any>) => {
		const filterField = filters.filterField || "documento";
		// Busca por documento não funciona com CPF/CNPJ formatado (pontos/traço) — manda só os dígitos.
		const textSearch = filterField === "documento" ? String(filters.textSearch || "").replace(/\D/g, "") : filters.textSearch || "";

		const params = new URLSearchParams();
		params.set("status", Array.isArray(filters.status) ? filters.status.join(",") : filters.status || "Todos");
		params.set("paymentMethod", filters.paymentMethod || "Todos");
		params.set("filterField", filterField);
		params.set("textSearch", textSearch);
		params.set("data", filters.data || "ano");
		if (filters.data === "periodo") {
			params.set("dataIni", filters.dataIni || "");
			params.set("dataFim", filters.dataFim || "");
		}
		params.set("pagina", "1");
		params.set("limit", String(limit));
		setSearchParams(params, { replace: true });
		setShouldFetch(true);
	};

	return (
		<>
			<LayoutBaseDePagina
				titulo="Acordos"
				subtitulo="Listagem de acordos firmados com locatários em fase de cobrança"
				barraDeFerramentas={
					<>
						<Tabs
							value={activeSector}
							onChange={(_, newSector) => {
								setSelectedSector(newSector);
								setShouldFetch(true);
							}}
							aria-label="Setor"
							sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", mx: 1, borderRadius: 1 }}
						>
							{allowedSectors.map((sector) => (
								<Tab key={sector} value={sector} label={getSectorLabel(sector)} />
							))}
						</Tabs>

						<ToolbarSinisterGeneric
							fieldsConfig={fieldsConfig}
							filterInitialParams={filtersFromSearchParams}
							onSearch={handleSearch}
							onClear={handleClearFilters}
						/>

						{canSyncBillets && (
							<Button size="small" variant="outlined" startIcon={<SyncOutlined />} sx={{ mx: 1 }} onClick={() => setOpenSyncBilletsModal(true)}>
								Sincronizar Boletos
							</Button>
						)}
					</>
				}
			>
				{hasViewPermission && (
					<TableAgreements
						rows={rows}
						totalCount={totalCount}
						page={pagina - 1}
						rowsPerPage={limit}
						onPageChange={(newPage) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("pagina", String(newPage + 1));
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
						onRowsPerPageChange={(newLimit) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("limit", String(newLimit));
									params.set("pagina", "1");
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
						onAgreementChanged={() => setShouldFetch(true)}
					/>
				)}
			</LayoutBaseDePagina>
			<LoadingComp isopen={isLoading} />

			<ModalSyncAgreementBillets
				open={openSyncBilletsModal}
				onClose={() => setOpenSyncBilletsModal(false)}
				onConfirm={() => setShouldFetch(true)}
			/>
		</>
	);
};
